import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Avatar,
  Tooltip,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Search,
  Send,
  Download,
  Print,
  MoreVert,
  Receipt,
  Schedule,
  CheckCircle,
  Warning,
  Error,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import invoicesService, { Invoice } from '../services/invoicesService';

const Invoices: React.FC = () => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedInvoice, setSelectedInvoice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState<Invoice | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({ open: false, message: '', severity: 'success' });

  // Remove the old interface since we're importing it from the service
  /*interface Invoice {
  id: string;
  invoiceNumber: string;
  client: {
    name: string;
    company: string;
    email: string;
  };
  project: string;
  amount: number;
  taxAmount: number;
  totalAmount: number;
  status: 'draft' | 'sent' | 'viewed' | 'partial' | 'paid' | 'overdue' | 'cancelled';
  dueDate: string;
  issueDate: string;
  paidDate?: string;
  paidAmount?: number;
  description: string;
  items: {
    description: string;
    quantity: number;
    rate: number;
    amount: number;
  }[];
  paymentTerms: string;
  notes?: string;
  createdBy: string;
  lastSent?: string;
}*/

  // Load invoices on component mount
  useEffect(() => {
    loadInvoices();
  }, []);

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm.trim()) {
        handleSearch();
      } else {
        loadInvoices();
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const loadInvoices = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await invoicesService.getInvoices();
      // Ensure data is an array
      setInvoices(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading invoices:', err);
      setError('Failed to load invoices. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await invoicesService.searchInvoices(searchTerm);
      // Ensure data is an array
      setInvoices(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error searching invoices:', err);
      setError('Failed to search invoices. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteInvoice = async () => {
    if (!invoiceToDelete) return;

    try {
      await invoicesService.deleteInvoice(invoiceToDelete.id);
      setInvoices(invoices.filter(item => item.id !== invoiceToDelete.id));
      setSnackbar({
        open: true,
        message: 'Invoice deleted successfully',
        severity: 'success'
      });
    } catch (err) {
      console.error('Error deleting invoice:', err);
      setSnackbar({
        open: true,
        message: 'Failed to delete invoice',
        severity: 'error'
      });
    } finally {
      setDeleteDialogOpen(false);
      setInvoiceToDelete(null);
    }
  };

  const handleStatusChange = async (invoiceId: string, newStatus: Invoice['status']) => {
    try {
      const updatedInvoice = await invoicesService.updateInvoiceStatus(invoiceId, newStatus);
      setInvoices(invoices.map(item => 
        item.id === invoiceId ? updatedInvoice : item
      ));
      setSnackbar({
        open: true,
        message: `Invoice status updated to ${newStatus}`,
        severity: 'success'
      });
    } catch (err) {
      console.error('Error updating invoice status:', err);
      setSnackbar({
        open: true,
        message: 'Failed to update invoice status',
        severity: 'error'
      });
    }
  };

  const getStatusColor = (status: Invoice['status']) => {
    switch (status) {
      case 'paid':
        return 'success';
      case 'partial':
        return 'info';
      case 'sent':
      case 'viewed':
        return 'primary';
      case 'draft':
        return 'default';
      case 'overdue':
        return 'error';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: Invoice['status']) => {
    switch (status) {
      case 'paid':
        return <CheckCircle />;
      case 'partial':
        return <Schedule />;
      case 'sent':
      case 'viewed':
        return <Send />;
      case 'draft':
        return <Receipt />;
      case 'overdue':
        return <Warning />;
      case 'cancelled':
        return <Error />;
      default:
        return undefined;
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, invoiceId: string) => {
    setAnchorEl(event.currentTarget);
    setSelectedInvoice(invoiceId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedInvoice(null);
  };

  // Ensure invoices is an array before using array methods
  const invoicesArray = Array.isArray(invoices) ? invoices : [];
  
  const filteredInvoices = invoicesArray.filter((item) => {
    const q = searchTerm.toLowerCase();
    const invoiceNumber = (item.invoiceNumber || '').toString().toLowerCase();
    const clientName = (item.client?.name || '').toString().toLowerCase();
    const clientCompany = (item.client?.company || '').toString().toLowerCase();
    const projectName = (item.project || '').toString().toLowerCase();
    const status = (item.status || '').toString().toLowerCase();
    return (
      invoiceNumber.includes(q) ||
      clientName.includes(q) ||
      clientCompany.includes(q) ||
      projectName.includes(q) ||
      status.includes(q)
    );
  });

  const totalInvoiced = invoicesArray.reduce((sum, item) => sum + (item.totalAmount || 0), 0);
  const totalPaid = invoicesArray.reduce((sum, item) => sum + (item.paidAmount || 0), 0);
  const totalOutstanding = totalInvoiced - totalPaid;
  const overdueCount = invoicesArray.filter(item => item.status === 'overdue').length;

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading invoices...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Invoice Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => navigate('/invoices/create')}
        >
          Create Invoice
        </Button>
      </Box>

      {/* Summary Cards */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 3 }}>
        <Card sx={{ minWidth: 200, flex: 1 }}>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Total Invoiced
            </Typography>
            <Typography variant="h5" component="div">
              {formatCurrency(totalInvoiced)}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 200, flex: 1 }}>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Total Paid
            </Typography>
            <Typography variant="h5" component="div" color="success.main">
              {formatCurrency(totalPaid)}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 200, flex: 1 }}>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Outstanding
            </Typography>
            <Typography variant="h5" component="div" color="warning.main">
              {formatCurrency(totalOutstanding)}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 200, flex: 1 }}>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Overdue
            </Typography>
            <Typography variant="h5" component="div" color="error.main">
              {overdueCount}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Search */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search invoices..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Invoices Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Invoice #</TableCell>
              <TableCell>Client</TableCell>
              <TableCell>Project</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Issue Date</TableCell>
              <TableCell>Due Date</TableCell>
              <TableCell>Payment</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredInvoices.map((item) => (
              <TableRow key={item.id} hover>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="medium">
                    {item.invoiceNumber || '—'}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {item.description || ''}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ width: 32, height: 32, fontSize: '0.875rem' }}>
                      {(item.client?.name || item.client?.company || item.invoiceNumber || '?')
                        .toString()
                        .charAt(0)
                        .toUpperCase()}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {item.client?.name || '—'}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {item.client?.company || '—'}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>{item.project || '—'}</TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">
                    {formatCurrency(item.totalAmount || 0)}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Tax: {formatCurrency(item.taxAmount || 0)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    icon={getStatusIcon(item.status || 'draft')}
                    label={(item.status || 'draft').toUpperCase()}
                    color={getStatusColor(item.status || 'draft')}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {item.issueDate ? new Date(item.issueDate).toLocaleDateString() : '—'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography 
                    variant="body2"
                    color={
                      item.dueDate && new Date(item.dueDate) < new Date() && (item.status || '') !== 'paid'
                        ? 'error.main'
                        : 'textPrimary'
                    }
                  >
                    {item.dueDate ? new Date(item.dueDate).toLocaleDateString() : '—'}
                  </Typography>
                </TableCell>
                <TableCell>
                  {item.paidAmount ? (
                    <Box>
                      <Typography variant="body2" color="success.main">
                        {formatCurrency(item.paidAmount)}
                      </Typography>
                      {item.paidDate && (
                        <Typography variant="caption" color="textSecondary">
                          {new Date(item.paidDate).toLocaleDateString()}
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      Unpaid
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Tooltip title="View Invoice">
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/invoices/${item.id}`)}
                    >
                      <Receipt />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edit Invoice">
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/invoices/${item.id}/edit`)}
                    >
                      <Edit />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="More Actions">
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuClick(e, item.id)}
                    >
                      <MoreVert />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleMenuClose}>
          <Send sx={{ mr: 1 }} /> Send Invoice
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <Download sx={{ mr: 1 }} /> Download PDF
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <Print sx={{ mr: 1 }} /> Print
        </MenuItem>
        <MenuItem 
          onClick={() => {
            const invoice = invoices.find(inv => inv.id === selectedInvoice);
            if (invoice) {
              setInvoiceToDelete(invoice);
              setDeleteDialogOpen(true);
            }
            handleMenuClose();
          }} 
          sx={{ color: 'error.main' }}
        >
          <Delete sx={{ mr: 1 }} /> Delete
        </MenuItem>
      </Menu>

      {filteredInvoices.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="textSecondary">
            No invoices found
          </Typography>
          <Typography color="textSecondary">
            {searchTerm ? 'Try adjusting your search terms' : 'Start by creating your first invoice'}
          </Typography>
        </Box>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Invoice</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete invoice {invoiceToDelete?.invoiceNumber}? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteInvoice} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Error Display */}
      {error && (
        <Snackbar
          open={!!error}
          autoHideDuration={6000}
          onClose={() => setError(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setError(null)}
            severity="error"
            sx={{ width: '100%' }}
          >
            {error}
          </Alert>
        </Snackbar>
      )}
    </Box>
  );
};

export default Invoices;